import { CustomerOrder } from '../../../../clients/customer-api/order/customer-order';
import {
  CouponLineItem,
  DamageWaiverLineItem,
  DeliveryLineItem,
  DiscountLineItem,
  FeeLineItem,
  PickupLineItem,
  ProductFeeLineItem,
  ProductLineItem,
  SecurityDepositLineItem,
  TaxLineItem,
} from '../../../../clients/quipli-service/financials/financials';
import {
  OrderAdditionalFee,
  OrderProductAdditionalFee,
  OrderType,
} from '../../../../clients/quipli-service/order/order';
import { convertToMoney, safeRound } from '../../../../util/util';
import { ProductType } from '../../../../clients/quipli-service/product/product';

export const generateProductLineItems = (order: CustomerOrder, currency: Dinero.Currency) => {
  const productFeeLineItems: ProductFeeLineItem[] = [];
  const productLineItems: ProductLineItem[] = order.products.map((product) => {
    const additionalFees = product.additionalFees.map(
      (fee: OrderProductAdditionalFee) =>
        ({
          productLineItemId: `${product.id}`,
          productFeeId: +fee.id,
          productId: +fee.productId,
          description: fee.feeTitle,
          type: 'ProductFee',
          percentage: fee.feeType === 'P' ? fee.feeValue || 0 : 0,
          taxable: fee.taxApplied,
          unitPrice: convertToMoney(+fee.feeAppliedValue, currency),
          amount: convertToMoney(+fee.feeAppliedValue, currency),
          recurring: fee.recurring,
          hasCustomAmount: !!fee.updatedAt,
          optional: false, // not needed
          isSelected: true, // not needed
        } as ProductFeeLineItem),
    );

    productFeeLineItems.push(...additionalFees);

    return {
      id: `${product.id}`,
      productId: +product.product.id,
      type: 'Product',
      description: product.product.name,
      transactionType: product.type,
      unitPrice: convertToMoney(+product.customerBuyingPrice || 0, currency),
      quantity: +product.quantity,
      taxable: !product.isTaxFree,
      tax: convertToMoney(+product.tax, currency),
      amount: convertToMoney(
        +product.customerBuyingPrice *
          +product.quantity *
          (product.discountPercentage ? +(1 - product.discountPercentage / 100) : 1),
        currency,
      ),
      hasCustomAmount: false,
    } as ProductLineItem;
  });

  return { productFeeLineItems, productLineItems };
};

export const generateFeeLineItems = (order: CustomerOrder, currency: Dinero.Currency) => {
  const feeLineItems: FeeLineItem[] = order.additionalFees.map(
    (fee: OrderAdditionalFee) =>
      ({
        id: fee.id,
        feeId: +fee.feeId,
        description: fee.feeTitle,
        type: 'Fee',
        percentage: fee.feeType === 'P' ? +fee.feeValue || 0 : 0,
        taxable: fee.taxApplied,
        optional: false, // not needed
        isSelected: true,
        unitPrice: convertToMoney(+fee.feeAppliedValue, currency),
        amount: convertToMoney(+fee.feeAppliedValue, currency),
        hasCustomAmount: !!fee.updatedAt,
        quantity: 1,
        recurring: fee.recurring,
      } as FeeLineItem),
  );

  return feeLineItems;
};

export const generateDeliveryFeeLineItem = (order: CustomerOrder, currency: Dinero.Currency) => {
  const deliveryFee: DeliveryLineItem = {
    type: 'Delivery',
    hasCustomAmount: false, // not needed
    description: 'Delivery',
    quantity: 1,
    taxable: false, // not needed
    unitPrice: convertToMoney(order.shippingCharged || 0, currency),
    amount: convertToMoney(order.shippingCharged || 0, currency),
    recurring: false, // not needed
  };

  return deliveryFee;
};

export const generatePickupFeeLineItem = (order: CustomerOrder, currency: Dinero.Currency) => {
  const pickupFee: PickupLineItem = {
    type: 'Pickup',
    hasCustomAmount: false, // not needed
    description: 'Pickup',
    quantity: 1,
    taxable: false, // not needed
    unitPrice: convertToMoney(order.pickupCharged || 0, currency),
    amount: convertToMoney(order.pickupCharged || 0, currency),
    recurring: false, // not needed
  };

  return pickupFee;
};

export const generateDamageWaiverLineItem = (order: CustomerOrder, currency: Dinero.Currency) => {
  if (order.damageWaiver && order.damageWaiver > 0) {
    return {
      type: 'DamageWaiver',
      hasCustomAmount: false, // not needed
      description: 'Damage Waiver',
      quantity: 1,
      taxable: false, // not needed
      unitPrice: convertToMoney(order.damageWaiver, currency),
      amount: convertToMoney(order.damageWaiver, currency),
      recurring: false, // not needed
      optional: false,
      isSelected: true,
    } as DamageWaiverLineItem;
  }

  return null;
};

export const generateAdditionalDiscountLineItem = (order: CustomerOrder, currency: Dinero.Currency) => {
  if (order.additionalDiscount && order.additionalDiscount > 0) {
    return {
      type: 'AdditionalDiscount',
      description: 'Additional Discount',
      quantity: 1,
      taxable: false, // not needed
      unitPrice: convertToMoney(-order.additionalDiscount, currency),
      amount: convertToMoney(-order.additionalDiscount, currency),
      recurring: false, // not needed
    } as DiscountLineItem;
  }

  return null;
};

export const generateTaxLineItem = (order: CustomerOrder, currency: Dinero.Currency) => {
  if (order.taxCharged && order.taxCharged > 0) {
    return {
      type: 'Tax',
      hasCustomAmount: false, // not needed
      hasCustomPercentage: false, // not needed
      description: 'Tax',
      quantity: 1,
      taxable: false, // not needed
      userIsExempted: false, // not needed
      percentage: safeRound((order.taxPercentage || 0) * 100, 2),
      unitPrice: convertToMoney(+order.taxCharged, currency),
      amount: convertToMoney(+order.taxCharged, currency),
      recurring: false, // not needed
      taxBreakdown: order.taxBreakdown,
    } as TaxLineItem;
  }

  return null;
};

export const generateCouponLineItem = (order: CustomerOrder, currency: Dinero.Currency) => {
  if (order.discountTotal && order.discountTotal > 0) {
    return {
      type: 'Coupon',
      description: 'Coupon',
      quantity: 1,
      taxable: false, // not needed
      unitPrice: convertToMoney(-order.discountTotal, currency),
      amount: convertToMoney(-order.discountTotal, currency),
      code: order.discountCoupon || '-NA-',
      productIds: [],
      recurring: false, // not needed
      isVip: false, // not needed
    } as CouponLineItem;
  }

  return null;
};

export const generateSecurityDepositLineItem = (order: CustomerOrder, currency: Dinero.Currency) => {
  if (order.rentalSecurity && order.rentalSecurity > 0) {
    return {
      type: 'SecurityDeposit',
      description: '',
      quantity: 1,
      taxable: false, // not needed
      unitPrice: convertToMoney(order.rentalSecurity, currency),
      amount: convertToMoney(order.rentalSecurity, currency),
      percentage: order.rentalSecurityPercentage,
      recurring: false, // not needed
    } as SecurityDepositLineItem;
  }

  return null;
};

export const generateCart = (order: CustomerOrder) => ({
  id: '',
  sessionId: '',
  items: order.products.map((p) => ({
    id: `${p.id}`,
    cartId: '',
    type: p.type,
    product: {
      ...p.product,
      ...(p.product.type === ProductType.Custom && {
        name: `${p.name}${p.description ? `: ${p.description}` : ''}`,
      }),
    },
    quantity: p.quantity,
    options: {},
    ...(p.type === OrderType.Rental && {
      startDate: p.rentalStartDate,
      endDate: p.rentalEndDate,
    }),
  })),
  meta: {
    selectedAddresses: {
      billing: order.billingAddress?.id,
      shipping: order.shippingAddress?.id,
    },
  },
});
